import React, { useMemo } from 'react';

import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics';

import { UNINITIALIZED_COMMON_ATTRIBUTES } from '../../../ai-answer-dialog/uninitialized-common-attributes';
import { AiBrand, AnswerCardType, DEFAULT_GAS_CHANNEL } from '../../../constants';
import { isHello } from '../../../is-hello';
import { sha256Hash } from '../../../sha256-hash';

import { OpenChatInputBox, type OpenChatInputBoxProps } from './open-chat-input-box';

export type FpsOpenChatInputBoxProps = Pick<
	OpenChatInputBoxProps,
	'query' | 'searchSessionId' | 'isVisible' | 'onVisibilityChange' | 'title' | 'onHideTransitionEnd'
> & {
	/** Tenant cloud id — used to derive `isHello` and reported on the events. */
	cloudId?: string;
	/**
	 * The product hosting the search (e.g. `confluence`). Used both as the
	 * `sourceProduct` analytics attribute and the AI-instrumentation `product`.
	 */
	sourceProduct?: string;
	/** The sub-product hosting the search, for AI instrumentation. */
	subproduct?: string;
	/** Products available to search, reported for parity with SAIN events. */
	availableProducts?: string[];
};

/**
 * Full Page Search (FPS) entry point for the "open chat" input box.
 *
 * The FPS results list renders the box **outside** any `AIAnswerContext`
 * provider, so it cannot derive its analytics `commonAttributes` from context
 * (they would be the uninitialised defaults — empty `source`/`query`). This
 * wrapper builds the `commonAttributes` from the FPS inputs and passes them
 * directly to `OpenChatInputBox`, bringing the fired events to parity with the
 * SAIN-on-FPS events for every field FPS can supply at chat-open time.
 *
 * Answer-derived fields (sources, answerHash, requestId, …) intentionally stay
 * at their defaults because no AI answer exists when the user opens chat from a
 * result.
 */
export const FpsOpenChatInputBox = ({
	query,
	searchSessionId,
	cloudId,
	sourceProduct,
	subproduct,
	availableProducts,
	isVisible = true,
	onVisibilityChange,
	title,
	onHideTransitionEnd,
}: FpsOpenChatInputBoxProps): JSX.Element => {
	const commonAttributes = useMemo(() => {
		// `queryHash`/`baseQueryHash` are passed through (not derived) by
		// `filterCommonAttributes`, so hash the query here for parity with SAIN
		// events. FPS has no separate transformed/base query, so both match —
		// compute the hash once and reuse it.
		const queryHash = query ? sha256Hash(query) : '';

		return {
			...UNINITIALIZED_COMMON_ATTRIBUTES,
			source: 'fps',
			query: query ?? '',
			searchSessionId: searchSessionId ?? '',
			queryHash,
			baseQueryHash: queryHash,
			// No SAIN answer behind the FPS box, so don't label it as a SAIN card.
			answerCardType: AnswerCardType.NONE,
			// The inline box opens Rovo Chat, so report the Rovo brand.
			brand: AiBrand.ROVO,
			// Derived from the tenant cloud id, matching how SAIN computes it.
			cloudId: cloudId ?? '',
			isHello: cloudId ? isHello(cloudId) : false,
			...(sourceProduct !== undefined ? { sourceProduct } : {}),
			// `extraAttributes` are spread (and sanitized) into the event's
			// attributes by `filterCommonAttributes`, mirroring SAIN's
			// `extraAnalyticsAttributes` for the values FPS can supply. Only
			// allowlisted, non-UGC keys are included here — the raw user query is
			// carried separately via `nonPrivacySafeAttributes.query`.
			extraAttributes: {
				isFullPageSearch: true,
				...(availableProducts !== undefined ? { availableProducts } : {}),
			},
		};
	}, [query, searchSessionId, cloudId, sourceProduct, availableProducts]);

	return (
		<AIEventsInstrumentationProvider
			config={{
				product: sourceProduct ?? 'unknown',
				subproduct: subproduct ?? 'unknown',
				aiFeatureName: 'fpsRovoChatInput',
				aiExperienceName: 'fpsRovoChatInput',
				proactiveGeneratedAI: 0,
				userGeneratedAI: 1,
				isAIFeature: 1,
				source: 'fps',
				channel: DEFAULT_GAS_CHANNEL,
			}}
		>
			<OpenChatInputBox
				source="fps"
				isVisible={isVisible}
				commonAttributes={commonAttributes}
				onVisibilityChange={onVisibilityChange}
				title={title}
				onHideTransitionEnd={onHideTransitionEnd}
			/>
		</AIEventsInstrumentationProvider>
	);
};
